import { Injectable, inject } from "@angular/core";
import { Subscription } from "rxjs";
import { WebRtcPeerService } from "./web-rtc-peer.service";
import { RecordingSource } from "../models/call.models";

@Injectable({ providedIn: "root" })
export class CallRecordingService {
    private readonly peer = inject(WebRtcPeerService);
    private recorder: MediaRecorder | null = null;
    private chunks: Blob[] = [];
    private canvasStream: MediaStream | null = null;
    private animationFrame = 0;
    private subscriptions = new Subscription();
    private audioContext: AudioContext | null = null;
    private audioDestination: MediaStreamAudioDestinationNode | null = null;
    private localAudioSource: MediaStreamAudioSourceNode | null = null;
    private remoteAudioSource: MediaStreamAudioSourceNode | null = null;
    private readonly canvas = document.createElement("canvas");
    private readonly sourceVideo = document.createElement("video");
    private resultBlob: Blob | null = null;
    private resultUrl: string | null = null;

    get active(): boolean { return this.recorder?.state === "recording"; }
    get previewUrl(): string | null { return this.resultUrl; }
    get resultType(): string { return this.resultBlob?.type ?? "video/webm"; }

    async start(source: RecordingSource): Promise<void> {
        if (this.active) return;
        this.clearResult();
        this.canvas.width = 1280;
        this.canvas.height = 720;
        this.sourceVideo.autoplay = true;
        this.sourceVideo.muted = true;
        this.sourceVideo.playsInline = true;
        this.audioContext = new AudioContext();
        await this.audioContext.resume();
        this.audioDestination = this.audioContext.createMediaStreamDestination();
        const visual$ = source === "self" ? this.peer.outgoingVisualStream$ : this.peer.remoteStream$;
        this.subscriptions = new Subscription();
        this.subscriptions.add(visual$.subscribe((stream) => this.setVisualStream(stream)));
        this.subscriptions.add(this.peer.localStream$.subscribe((stream) => this.connectAudio("local", stream)));
        this.subscriptions.add(this.peer.remoteStream$.subscribe((stream) => this.connectAudio("remote", stream)));
        this.canvasStream = this.canvas.captureStream(30);
        const recordingStream = new MediaStream([
            ...this.canvasStream.getVideoTracks(),
            ...this.audioDestination.stream.getAudioTracks(),
        ]);
        const mimeType = this.supportedMimeType();
        this.chunks = [];
        try {
            this.recorder = mimeType ? new MediaRecorder(recordingStream, { mimeType, videoBitsPerSecond: 3_000_000 }) : new MediaRecorder(recordingStream);
            this.recorder.ondataavailable = ({ data }) => { if (data.size) this.chunks.push(data); };
            this.startRenderer();
            this.recorder.start(1000);
        } catch (error) {
            this.releaseLiveResources();
            throw error;
        }
    }

    stop(): Promise<Blob> {
        const recorder = this.recorder;
        if (!recorder || recorder.state === "inactive") return Promise.reject(new Error("There is no active recording."));
        return new Promise<Blob>((resolve, reject) => {
            recorder.onerror = () => { this.releaseLiveResources(); reject(new Error("Recording failed.")); };
            recorder.onstop = () => {
                const type = recorder.mimeType || this.chunks[0]?.type || "video/webm";
                this.resultBlob = new Blob(this.chunks, { type });
                this.resultUrl = URL.createObjectURL(this.resultBlob);
                this.releaseLiveResources();
                resolve(this.resultBlob);
            };
            recorder.stop();
        });
    }

    download(): void {
        if (!this.resultBlob || !this.resultUrl) return;
        const extension = this.resultBlob.type.includes("mp4") ? "mp4" : "webm";
        const anchor = document.createElement("a");
        anchor.href = this.resultUrl;
        anchor.download = `${Date.now()}_call_recording.${extension}`;
        anchor.click();
    }

    clearResult(): void {
        if (this.resultUrl) URL.revokeObjectURL(this.resultUrl);
        this.resultUrl = null;
        this.resultBlob = null;
    }

    private setVisualStream(stream: MediaStream | null): void {
        if (this.sourceVideo.srcObject === stream) return;
        this.sourceVideo.srcObject = stream;
        if (stream) void this.sourceVideo.play().catch(() => undefined);
    }
    private connectAudio(side: "local" | "remote", stream: MediaStream | null): void {
        if (!this.audioContext || !this.audioDestination) return;
        const existing = side === "local" ? this.localAudioSource : this.remoteAudioSource;
        existing?.disconnect();
        const tracks = stream?.getAudioTracks().filter(({ readyState }) => readyState === "live") ?? [];
        const next = tracks.length ? this.audioContext.createMediaStreamSource(new MediaStream(tracks)) : null;
        next?.connect(this.audioDestination);
        if (side === "local") this.localAudioSource = next; else this.remoteAudioSource = next;
    }
    private startRenderer(): void {
        const context = this.canvas.getContext("2d", { alpha: false });
        if (!context) throw new Error("Recording canvas is unavailable.");
        const draw = () => {
            context.fillStyle = "black";
            context.fillRect(0, 0, this.canvas.width, this.canvas.height);
            if (this.sourceVideo.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA && this.sourceVideo.videoWidth && this.sourceVideo.videoHeight) {
                const scale = Math.min(this.canvas.width / this.sourceVideo.videoWidth, this.canvas.height / this.sourceVideo.videoHeight);
                const width = this.sourceVideo.videoWidth * scale;
                const height = this.sourceVideo.videoHeight * scale;
                context.drawImage(this.sourceVideo, (this.canvas.width - width) / 2, (this.canvas.height - height) / 2, width, height);
            }
            this.animationFrame = requestAnimationFrame(draw);
        };
        draw();
    }
    private supportedMimeType(): string {
        return ["video/webm;codecs=vp9,opus", "video/webm;codecs=vp8,opus", "video/webm", "video/mp4"].find((type) => MediaRecorder.isTypeSupported(type)) ?? "";
    }
    private releaseLiveResources(): void {
        cancelAnimationFrame(this.animationFrame);
        this.animationFrame = 0;
        this.subscriptions.unsubscribe();
        this.localAudioSource?.disconnect();
        this.remoteAudioSource?.disconnect();
        this.localAudioSource = null;
        this.remoteAudioSource = null;
        for (const track of this.canvasStream?.getTracks() ?? []) track.stop();
        this.canvasStream = null;
        this.sourceVideo.srcObject = null;
        void this.audioContext?.close();
        this.audioContext = null;
        this.audioDestination = null;
        this.recorder = null;
        this.chunks = [];
    }
}
